import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getOverviewStats, getUserGrowth, getWhatsAppStats, getMessageVolumeStats, getConversationAnalytics, getRealtimeMetrics } from '../../services/adminApi';
import apiClient from '../../services/api';
import StatCard from '../components/StatCard';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const isSuperadmin = user?.role === 'superadmin';
  const [overview, setOverview] = useState(null);
  const [userGrowth, setUserGrowth] = useState([]);
  const [whatsappStats, setWhatsAppStats] = useState(null);
  const [messageVolume, setMessageVolume] = useState([]);
  const [conversationAnalytics, setConversationAnalytics] = useState(null);
  const [realtimeMetrics, setRealtimeMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [growthPeriod, setGrowthPeriod] = useState('monthly');
  const [messagePeriod, setMessagePeriod] = useState('daily');

  // Superadmin-only: unresolvable orphan-approved posts (week fully booked
  // or scheduled_date in the past). Lazy-loaded; the panel itself is hidden
  // when the count is zero so admins are not distracted by an empty card.
  const [unresolvableCount, setUnresolvableCount] = useState(0);
  const [unresolvableExpanded, setUnresolvableExpanded] = useState(false);
  const [unresolvableList, setUnresolvableList] = useState([]);
  const [unresolvableLoading, setUnresolvableLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [growthPeriod, messagePeriod]);

  // Fetch the unresolvable-orphan count for superadmins. Uses the
  // publication-queue/stats endpoint (admin-scoped) which now exposes
  // unresolvable_orphan_count alongside the existing daily counts.
  useEffect(() => {
    if (!isSuperadmin) return;
    apiClient
      .get('/api/v1/admin/publication-queue/stats')
      .then((res) => {
        setUnresolvableCount(res.data?.unresolvable_orphan_count || 0);
      })
      .catch((err) => {
        // Non-fatal — the rest of the dashboard renders regardless.
        console.error('Failed to load unresolvable orphan count:', err);
      });
  }, [isSuperadmin]);

  const fetchUnresolvableList = async () => {
    setUnresolvableLoading(true);
    try {
      const res = await apiClient.get(
        '/api/v1/admin/publication-slots/unresolvable-orphans',
      );
      setUnresolvableList(res.data || []);
    } catch (err) {
      console.error('Failed to load unresolvable orphans list:', err);
    } finally {
      setUnresolvableLoading(false);
    }
  };

  const toggleUnresolvable = () => {
    if (!unresolvableExpanded && unresolvableList.length === 0) {
      fetchUnresolvableList();
    }
    setUnresolvableExpanded((v) => !v);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [overviewRes, growthRes, whatsappRes, messageRes, convRes, realtimeRes] = await Promise.all([
        getOverviewStats(),
        getUserGrowth(growthPeriod),
        getWhatsAppStats(),
        getMessageVolumeStats(messagePeriod),
        getConversationAnalytics(),
        getRealtimeMetrics()
      ]);
      setOverview(overviewRes.data);
      setUserGrowth(growthRes.data);
      setWhatsAppStats(whatsappRes.data);
      setMessageVolume(messageRes.data);
      setConversationAnalytics(convRes.data);
      setRealtimeMetrics(realtimeRes.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <h2>Overview Statistics</h2>

      {/* Superadmin-only alert: posts that the orphan-resweep cannot place
          (their week is fully booked or scheduled_date is in the past).
          Hidden when the count is zero so the panel does not add noise. */}
      {isSuperadmin && unresolvableCount > 0 && (
        <div className="dashboard-section unresolvable-orphans-panel">
          <div className="section-header-row">
            <div>
              <h3>
                ⚠️ {t('admin.slots.unresolvableOrphans.title')}
                <span className="orphan-badge-count">{unresolvableCount}</span>
              </h3>
              <p className="section-description">
                {t('admin.slots.unresolvableOrphans.description')}
              </p>
            </div>
            <button className="section-link-btn" onClick={toggleUnresolvable}>
              {unresolvableExpanded ? '−' : '+'}
            </button>
          </div>
          {unresolvableExpanded && (
            <div className="unresolvable-orphans-table">
              {unresolvableLoading && <div>Loading…</div>}
              {!unresolvableLoading && unresolvableList.length === 0 && (
                <div>{t('admin.slots.unresolvableOrphans.empty')}</div>
              )}
              {!unresolvableLoading && unresolvableList.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th>{t('admin.slots.unresolvableOrphans.colPostId')}</th>
                      <th>{t('admin.slots.unresolvableOrphans.colUser')}</th>
                      <th>{t('admin.slots.unresolvableOrphans.colScheduledDate')}</th>
                      <th>{t('admin.slots.unresolvableOrphans.colMessage')}</th>
                      <th>{t('admin.slots.unresolvableOrphans.colLastAttempt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unresolvableList.map((row) => (
                      <tr key={row.post_id}>
                        <td>
                          <button
                            className="section-link-btn"
                            onClick={() => navigate(`/admin/users/${row.user_id}`)}
                          >
                            #{row.post_id}
                          </button>
                        </td>
                        <td>{row.user_email || `user ${row.user_id}`}</td>
                        <td>{row.scheduled_date || '—'}</td>
                        <td>{row.status_message || '—'}</td>
                        <td>
                          {row.last_attempt_at
                            ? new Date(row.last_attempt_at).toLocaleString()
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      <div className="stats-grid">
        <StatCard
          icon="👥"
          label="Total Users"
          value={overview?.total_users || 0}
        />
        <StatCard
          icon="✅"
          label="Active Users (30d)"
          value={overview?.active_users_30d || 0}
        />
        <StatCard
          icon="📱"
          label="WhatsApp Numbers"
          value={overview?.total_whatsapp_numbers || 0}
        />
        <StatCard
          icon="💬"
          label="Messages Today"
          value={overview?.messages_today || 0}
        />
        <StatCard
          icon="📊"
          label="Messages (Week)"
          value={overview?.messages_week || 0}
        />
        <StatCard
          icon="📈"
          label="Messages (Month)"
          value={overview?.messages_month || 0}
        />
      </div>

      {/* ── Social Connections ── */}
      <div className="dashboard-section">
        <div className="section-header-row">
          <h3>📱 Social Connections</h3>
          <button className="section-link-btn" onClick={() => navigate('/admin/social-accounts')}>
            View all →
          </button>
        </div>
        {/* ── Instagram ── */}
        <h4 className="social-platform-title">📸 Instagram</h4>
        <div className="social-stats-grid">
          <div className="social-stat-card">
            <div className="social-stat-icon">📸</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.total_instagram_accounts || 0}</div>
              <div className="social-stat-label">Total Accounts</div>
            </div>
          </div>
          <div className="social-stat-card social-stat-active">
            <div className="social-stat-icon">✅</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.active_instagram_accounts || 0}</div>
              <div className="social-stat-label">Active (Valid Token)</div>
            </div>
          </div>
          <div className="social-stat-card social-stat-expired">
            <div className="social-stat-icon">⚠️</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.expired_tokens || 0}</div>
              <div className="social-stat-label">Expired / No Token</div>
            </div>
          </div>
        </div>

        {/* ── TikTok ── */}
        <h4 className="social-platform-title">🎵 TikTok</h4>
        <div className="social-stats-grid">
          <div className="social-stat-card">
            <div className="social-stat-icon">🎵</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.total_tiktok_accounts || 0}</div>
              <div className="social-stat-label">Total Accounts</div>
            </div>
          </div>
          <div className="social-stat-card social-stat-active">
            <div className="social-stat-icon">✅</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.active_tiktok_accounts || 0}</div>
              <div className="social-stat-label">Active (Valid Token)</div>
            </div>
          </div>
          <div className="social-stat-card social-stat-expired">
            <div className="social-stat-icon">⚠️</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.expired_tiktok_tokens || 0}</div>
              <div className="social-stat-label">Expired / No Token</div>
            </div>
          </div>
        </div>

        {/* ── Facebook ── */}
        <h4 className="social-platform-title">📘 Facebook</h4>
        <div className="social-stats-grid">
          <div className="social-stat-card">
            <div className="social-stat-icon">📘</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.total_facebook_pages || 0}</div>
              <div className="social-stat-label">Total Pages</div>
            </div>
          </div>
          <div className="social-stat-card social-stat-active">
            <div className="social-stat-icon">✅</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.active_facebook_pages || 0}</div>
              <div className="social-stat-label">Active (Valid Token)</div>
            </div>
          </div>
          <div className="social-stat-card social-stat-expired">
            <div className="social-stat-icon">⚠️</div>
            <div className="social-stat-body">
              <div className="social-stat-value">{overview?.expired_facebook_tokens || 0}</div>
              <div className="social-stat-label">No Token</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Video Pipeline ── */}
      <div className="dashboard-section">
        <div className="section-header-row">
          <h3>🎬 Video Pipeline</h3>
          <button className="section-link-btn" onClick={() => navigate('/admin/scheduled-videos')}>
            View all →
          </button>
        </div>
        <div className="pipeline-stats-grid">
          <div className="pipeline-stat-card">
            <div className="pipeline-stat-value">{overview?.total_scheduled_posts || 0}</div>
            <div className="pipeline-stat-label">Total Posts</div>
          </div>
          <div className="pipeline-stat-card pipeline-published">
            <div className="pipeline-stat-value">{overview?.posts_published || 0}</div>
            <div className="pipeline-stat-label">Published</div>
          </div>
          <div className="pipeline-stat-card pipeline-active">
            <div className="pipeline-stat-value">{overview?.posts_in_pipeline || 0}</div>
            <div className="pipeline-stat-label">In Pipeline</div>
            {(overview?.posts_in_pipeline || 0) > 0 && (
              <div className="pipeline-active-indicator">
                <span className="pipeline-dot" /> Active
              </div>
            )}
          </div>
          <div className="pipeline-stat-card pipeline-failed">
            <div className="pipeline-stat-value">{overview?.posts_failed || 0}</div>
            <div className="pipeline-stat-label">Failed</div>
          </div>
          <div className="pipeline-stat-card pipeline-today">
            <div className="pipeline-stat-value">{overview?.posts_published_today || 0}</div>
            <div className="pipeline-stat-label">Published Today</div>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <h3>User Growth</h3>
        <div className="period-selector" role="group" aria-label="Select time period for user growth chart">
          <button
            className={growthPeriod === 'daily' ? 'active' : ''}
            onClick={() => setGrowthPeriod('daily')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setGrowthPeriod('daily');
              }
            }}
            aria-label="Show daily user growth"
            aria-pressed={growthPeriod === 'daily'}
          >
            Daily
          </button>
          <button
            className={growthPeriod === 'weekly' ? 'active' : ''}
            onClick={() => setGrowthPeriod('weekly')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setGrowthPeriod('weekly');
              }
            }}
            aria-label="Show weekly user growth"
            aria-pressed={growthPeriod === 'weekly'}
          >
            Weekly
          </button>
          <button
            className={growthPeriod === 'monthly' ? 'active' : ''}
            onClick={() => setGrowthPeriod('monthly')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setGrowthPeriod('monthly');
              }
            }}
            aria-label="Show monthly user growth"
            aria-pressed={growthPeriod === 'monthly'}
          >
            Monthly
          </button>
          <button
            className={growthPeriod === 'yearly' ? 'active' : ''}
            onClick={() => setGrowthPeriod('yearly')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setGrowthPeriod('yearly');
              }
            }}
            aria-label="Show yearly user growth"
            aria-pressed={growthPeriod === 'yearly'}
          >
            Yearly
          </button>
        </div>
        <div className="chart-container">
          {userGrowth.length > 0 ? (
            <div className="growth-chart">
              {userGrowth.map((item, idx) => (
                <div key={idx} className="growth-bar">
                  <div className="bar-label">{item.date}</div>
                  <div className="bar-wrapper">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${(item.count / Math.max(...userGrowth.map(i => i.count))) * 100}%`
                      }}
                    />
                  </div>
                  <div className="bar-value">{item.count}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data">No data available</div>
          )}
        </div>
      </div>

      {whatsappStats && (
        <div className="dashboard-section">
          <h3>WhatsApp Connections</h3>
          <div className="whatsapp-stats">
            <div className="whatsapp-stat-item">
              <div className="stat-label">Customer Care</div>
              <div className="stat-value-large">{whatsappStats.customer_care_count}</div>
            </div>
            <div className="whatsapp-stat-item">
              <div className="stat-label">Tutor</div>
              <div className="stat-value-large">{whatsappStats.tutor_count}</div>
            </div>
          </div>
          {whatsappStats.connections_by_day && whatsappStats.connections_by_day.length > 0 && (
            <div className="chart-container">
              <h4>Connections Over Time (Last 30 Days)</h4>
              <div className="growth-chart">
                {whatsappStats.connections_by_day.map((item, idx) => (
                  <div key={idx} className="growth-bar">
                    <div className="bar-label">{new Date(item.date).toLocaleDateString()}</div>
                    <div className="bar-wrapper">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${(item.count / Math.max(...whatsappStats.connections_by_day.map(i => i.count), 1)) * 100}%`
                        }}
                      />
                    </div>
                    <div className="bar-value">{item.count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="dashboard-section">
        <h3>Message Volume</h3>
        <div className="period-selector" role="group" aria-label="Select time period for message volume chart">
          <button
            className={messagePeriod === 'daily' ? 'active' : ''}
            onClick={() => setMessagePeriod('daily')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMessagePeriod('daily');
              }
            }}
            aria-label="Show daily message volume"
            aria-pressed={messagePeriod === 'daily'}
          >
            Daily
          </button>
          <button
            className={messagePeriod === 'weekly' ? 'active' : ''}
            onClick={() => setMessagePeriod('weekly')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMessagePeriod('weekly');
              }
            }}
            aria-label="Show weekly message volume"
            aria-pressed={messagePeriod === 'weekly'}
          >
            Weekly
          </button>
          <button
            className={messagePeriod === 'monthly' ? 'active' : ''}
            onClick={() => setMessagePeriod('monthly')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMessagePeriod('monthly');
              }
            }}
            aria-label="Show monthly message volume"
            aria-pressed={messagePeriod === 'monthly'}
          >
            Monthly
          </button>
        </div>
        <div className="chart-container">
          {messageVolume.length > 0 ? (
            <div className="message-volume-chart">
              {messageVolume.map((item, idx) => {
                const maxVolume = Math.max(...messageVolume.map(i => Math.max(i.sent, i.received)), 1);
                return (
                  <div key={idx} className="message-volume-item">
                    <div className="volume-label">{new Date(item.date).toLocaleDateString()}</div>
                    <div className="volume-bars">
                      <div className="volume-bar-wrapper">
                        <div className="volume-bar-label">Sent</div>
                        <div className="volume-bar-container">
                          <div
                            className="volume-bar sent"
                            style={{ width: `${(item.sent / maxVolume) * 100}%` }}
                          />
                          <span className="volume-value">{item.sent}</span>
                        </div>
                      </div>
                      <div className="volume-bar-wrapper">
                        <div className="volume-bar-label">Received</div>
                        <div className="volume-bar-container">
                          <div
                            className="volume-bar received"
                            style={{ width: `${(item.received / maxVolume) * 100}%` }}
                          />
                          <span className="volume-value">{item.received}</span>
                        </div>
                      </div>
                    </div>
                    {item.by_agent_type && Object.keys(item.by_agent_type).length > 0 && (
                      <div className="agent-type-breakdown">
                        {Object.entries(item.by_agent_type).map(([agentType, count]) => (
                          <span key={agentType} className="agent-type-tag">
                            {agentType}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-data">No message data available</div>
          )}
        </div>
      </div>

      {conversationAnalytics && (
        <div className="dashboard-section">
          <h3>Conversation Analytics</h3>
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-label">Average Duration</div>
              <div className="analytics-value">
                {conversationAnalytics.avg_duration > 0
                  ? `${Math.round(conversationAnalytics.avg_duration)}s`
                  : 'N/A'}
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-label">Escalation Rate</div>
              <div className="analytics-value">
                {conversationAnalytics.escalation_rate.toFixed(2)}%
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-label">Average Response Time</div>
              <div className="analytics-value">
                {conversationAnalytics.avg_response_time > 0
                  ? `${Math.round(conversationAnalytics.avg_response_time)}s`
                  : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}

      {realtimeMetrics && (
        <div className="dashboard-section">
          <h3>Real-time Metrics</h3>
          <div className="realtime-grid">
            <div className="realtime-card">
              <div className="realtime-label">Active Conversations</div>
              <div className="realtime-value">{realtimeMetrics.active_conversations}</div>
              <div className="realtime-subtitle">Last hour</div>
            </div>
            <div className="realtime-card">
              <div className="realtime-label">Queue Length</div>
              <div className="realtime-value">{realtimeMetrics.queue_length}</div>
              <div className="realtime-subtitle">Pending messages</div>
            </div>
            <div className="realtime-card">
              <div className="realtime-label">System Health</div>
              <div className={`realtime-value ${realtimeMetrics.system_health === 'healthy' ? 'healthy' : 'degraded'}`}>
                {realtimeMetrics.system_health === 'healthy' ? '✓ Healthy' : '⚠ Degraded'}
              </div>
              <div className="realtime-subtitle">
                DB: {realtimeMetrics.database_healthy ? '✓' : '✗'} | 
                Qdrant: {realtimeMetrics.qdrant_healthy ? '✓' : '✗'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;

